import { Link, useNavigate } from "react-router-dom";
import { Wordmark } from "@/components/Wordmark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Icons } from "@/lib/icons";
import { useAuth } from "@/contexts/AuthContext";
import { PRIMARY_SCREENINGS } from "@/lib/screenings";
import { useEffect, useMemo, useState } from "react";
import { CookieNotice } from "@/components/CookieNotice";
import { PageHead } from "@/components/PageHead";
import nameLight from "@/assets/claira-name-light.png";
import nameDark from "@/assets/claira-name-dark.png";

const features = [
  { icon: Icons.brain, title: "Validated screenings", desc: "PHQ-9, GAD-7, ASRS, PC-PTSD-5, OCI-R, AQ-10 — the same tools clinicians use.", pro: false },
  { icon: Icons.chat, title: "Talk to Claira", desc: "A private AI companion trained in CBT-informed support, with crisis safety built in.", pro: true },
  { icon: Icons.journal, title: "Reflective journaling", desc: "Write or record audio, tagged with mood, kept private to you.", pro: true },
  { icon: Icons.report, title: "GP-ready reports", desc: "Download a clinical-language PDF to share with your doctor — no account required.", pro: false },
  { icon: Icons.heart, title: "Trends over time", desc: "See how your mind has shifted over weeks and months.", pro: true },
  { icon: Icons.shield, title: "Privacy-first", desc: "Encrypted in transit and at rest. Anonymous by default. You can delete everything any time.", pro: false },
];

const FEATURED_SCREENING_IDS = new Set(["phq9", "gad7", "who5"]);

const WELLNESS_TOOLS = [
  { to: "/triage", title: "Not sure where to start?", desc: "A gentle 30-second triage that suggests which validated screening fits.", icon: Icons.brain },
  { to: "/mood", title: "Daily mood check-in", desc: "One slider, one word. Watch the patterns build over weeks.", icon: Icons.heart },
  { to: "/grounding", title: "Grounding & breathing", desc: "Short evidence-based exercises for a wave of anxiety or overwhelm.", icon: Icons.shield },
  { to: "/learn", title: "Plain-English explainers", desc: "What each screening actually measures, in everyday language.", icon: Icons.report },
  { to: "/support-directory", title: "Crisis & support directory", desc: "UK helplines, text-lines and routes into NHS support.", icon: Icons.chat },
  { to: "/screenings", title: "All validated screenings", desc: "Browse every clinical screening Claira offers.", icon: Icons.clinical },
];
const FEATURED_TOOLS = 3;

export default function Index() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [showAllScreenings, setShowAllScreenings] = useState(false);
  const [showAllTools, setShowAllTools] = useState(false);

  const visibleScreenings = useMemo(() => {
    const featured = PRIMARY_SCREENINGS.filter((s) => FEATURED_SCREENING_IDS.has(s.id));
    if (!showAllScreenings) return featured;
    const rest = PRIMARY_SCREENINGS.filter((s) => !FEATURED_SCREENING_IDS.has(s.id));
    return [...featured, ...rest];
  }, [showAllScreenings]);
  const hiddenScreeningCount = PRIMARY_SCREENINGS.length - FEATURED_SCREENING_IDS.size;

  const visibleTools = showAllTools ? WELLNESS_TOOLS : WELLNESS_TOOLS.slice(0, FEATURED_TOOLS);

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);


  return (
    <div className="min-h-screen bg-background">
      <PageHead
        title="Claira — Clinical mental-health screening, free & anonymous"
        description="Free, anonymous, clinically-grounded mental-health screenings (PHQ-9, GAD-7 and more). Plus a warm AI companion, journaling and curated UK support."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "Claira — mental-health screening",
          about: "Validated mental-health screenings for UK adults",
          audience: { "@type": "PeopleAudience", geographicArea: { "@type": "Country", name: "United Kingdom" } },
          publisher: { "@id": "https://claira.uk/#org" },
        }}
      />
      <header className="absolute top-0 left-0 right-0 z-10 safe-top safe-left safe-right">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost"><Link to={user ? "/dashboard" : "/auth"}>{user ? "Open app" : "Sign in"}</Link></Button>
          </div>
        </div>
      </header>

      <main>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-brand-mist/40 blur-3xl animate-breathe" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-brand-blush/40 blur-3xl animate-breathe" />
        <div className="relative mx-auto max-w-5xl px-6 pt-32 pb-20 md:pt-40 md:pb-32 text-center">
          <div className="mb-8 flex justify-center">
            <Wordmark
              size="xl"
              variant="main"
              className="[&_img]:!h-48 md:[&_img]:!h-64"
            />
          </div>
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/60 px-4 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">
            <Icons.clinical className="h-3.5 w-3.5 text-primary" />
            Clinically-grounded mental health screening, beautifully reimagined
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold leading-[1.05] tracking-tight text-foreground">
            Meet{" "}
            <img
              src={nameLight}
              alt="Claira"
              className="inline dark:hidden h-[0.72em] w-auto object-contain select-none align-[-0.05em] mx-1"
              draggable={false}
              width={320}
              height={96}
              fetchPriority="high"
            />
            <img
              src={nameDark}
              alt="Claira"
              className="hidden dark:inline h-[0.72em] w-auto object-contain select-none align-[-0.05em] mx-1"
              draggable={false}
              width={320}
              height={96}
              fetchPriority="high"
            />.
            <br />
            <span className="text-muted-foreground italic font-normal">Clarity. Care. Connection.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Free, anonymous mental-health screenings using validated clinical instruments. Then, if you'd like, continue with <span className="font-medium text-foreground">Claira Plus</span> — a warm AI companion, private journaling, and curated resources between check-ins.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="bg-gradient-brand text-primary-foreground shadow-soft hover:shadow-glow transition-shadow rounded-full px-8 h-12">
              <Link to="/screenings">Take a screening <Icons.next className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-8 h-12">
              <Link to={user ? "/subscribe" : "/auth"}>{user ? "Explore Claira Plus" : "Create your account"}</Link>
            </Button>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">Screenings stay free, forever. Not a medical diagnosis. In crisis? Call Samaritans free on 116 123 (24/7) or text SHOUT to 85258.</p>
        </div>
      </section>

      {/* No sign-up needed */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Icons.check className="h-3.5 w-3.5" /> No sign-up needed
          </div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold">Free, always</h2>
          <p className="mt-3 text-muted-foreground">Use any of these without an account — validated clinical screenings and supportive wellness tools.</p>
        </div>

        {/* Validated Screenings */}
        <div className="mb-12">
          <div className="mb-6 flex items-end justify-between gap-4">
            <h3 className="font-display text-2xl font-semibold">Validated Screenings</h3>
            <p className="hidden sm:block text-sm text-muted-foreground">The same tools clinicians use.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleScreenings.map((s) => (
              <Link key={s.id} to={`/screening/${s.id}`} className="group relative rounded-3xl border border-border/60 bg-card p-6 shadow-card transition-colors hover:border-primary/40 hover:bg-accent/20">
                <span className="absolute right-4 top-4 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Free</span>
                <div className="text-xs font-medium uppercase tracking-wider text-accent-foreground/70">{s.category}</div>
                <div className="mt-2 font-display text-xl font-semibold">{s.title}</div>
                <div className="mt-1 text-sm text-muted-foreground">{s.fullName}</div>
                <div className="mt-4 text-xs text-muted-foreground">{s.duration}</div>
              </Link>
            ))}
          </div>
          {hiddenScreeningCount > 0 && (
            <div className="mt-6 flex justify-center">
              <Button variant="outline" className="rounded-full" onClick={() => setShowAllScreenings((v) => !v)}>
                {showAllScreenings ? "Show fewer" : `Show all ${PRIMARY_SCREENINGS.length} screenings`}
              </Button>
            </div>
          )}
        </div>

        {/* Wellness Tools */}
        <div>
          <div className="mb-6 flex items-end justify-between gap-4">
            <h3 className="font-display text-2xl font-semibold">Wellness Tools</h3>
            <p className="hidden sm:block text-sm text-muted-foreground">Gentle support between check-ins.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleTools.map((t) => (
              <Link key={t.to} to={t.to} className="group relative rounded-3xl border border-border/60 bg-card p-6 shadow-card transition-colors hover:border-primary/40 hover:bg-accent/20">
                <span className="absolute right-4 top-4 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Free</span>
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-brand text-primary-foreground">
                  <t.icon className="h-4 w-4" />
                </div>
                <div className="mt-3 font-display text-lg font-semibold">{t.title}</div>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
                <div className="mt-3 text-xs text-primary inline-flex items-center gap-1">Open <Icons.next className="h-3 w-3" /></div>
              </Link>
            ))}
          </div>
          {WELLNESS_TOOLS.length > FEATURED_TOOLS && (
            <div className="mt-6 flex justify-center">
              <Button variant="outline" className="rounded-full" onClick={() => setShowAllTools((v) => !v)}>
                {showAllTools ? "Show fewer" : `Show all ${WELLNESS_TOOLS.length} tools`}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Claira Plus */}
      <section className="mx-auto max-w-5xl px-6 pb-20">
        <div className="relative overflow-hidden rounded-[2rem] border border-primary/20 bg-gradient-brand/10 p-8 md:p-12 shadow-card">
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-brand-mist/40 blur-3xl" />
          <div className="relative grid gap-8 md:grid-cols-2 md:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <Icons.premium className="h-3.5 w-3.5" /> Claira Plus
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight">Continuous, compassionate support</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Screenings stay free. Plus adds an always-available AI companion, voice & text journaling,
                personalised resources, and trends — so you're never alone with what you're carrying.
              </p>
              <div className="mt-6 flex flex-wrap items-baseline gap-3">
                <div className="font-display text-3xl font-semibold">£4.99<span className="text-base text-muted-foreground font-sans"> / month</span></div>
                <div className="text-sm text-muted-foreground">or £39.99 / year · save 33%</div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-gradient-brand rounded-full px-6 h-11">
                  <Link to={user ? "/subscribe" : "/auth"}>Start 7-day free trial</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full px-6 h-11">
                  <Link to="/screenings">Try a screening first</Link>
                </Button>
              </div>
            </div>
            <ul className="space-y-3">
              {[
                "Unlimited conversations with Claira",
                "Voice & text journaling with gentle reflections",
                "Personalised, evidence-based resources",
                "Trends and patterns across your check-ins",
                "Private, encrypted, GDPR-protected",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 rounded-2xl bg-card/70 p-3">
                  <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-primary shrink-0">
                    <Icons.check className="h-3.5 w-3.5" />
                  </div>
                  <span className="text-sm text-foreground/90">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-gradient-mist/40 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-semibold">Everything Claira offers</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl bg-card p-6 shadow-card">
                <div className="mb-4 flex items-center justify-between">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-brand text-primary-foreground">
                    <f.icon className="h-5 w-5" />
                  </div>
                  {f.pro && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary">
                      <Icons.premium className="h-3 w-3" /> Pro
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </main>

      <footer className="border-t border-border/60 py-10 text-center text-sm text-muted-foreground">
        <Wordmark size="sm" />
        <p className="mt-3">© {new Date().getFullYear()} Claira. Clarity. Care. Connection.</p>
        <p className="mt-1 text-xs">Not a medical device. For wellbeing support only.</p>
        <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs">
          <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
          <Link to="/cookie-policy" className="hover:text-foreground">Cookie Policy</Link>
          <Link to="/legal" className="hover:text-foreground">Legal</Link>
          <Link to="/security" className="hover:text-foreground">Security</Link>
          <Link to="/support" className="hover:text-foreground">Support</Link>
          <a href="mailto:help@claira.uk" className="hover:text-foreground">help@claira.uk</a>
        </div>
        <p className="mt-2 text-xs opacity-60">Registered in England and Wales · ICO registration: [to be inserted]</p>
      </footer>
      <CookieNotice />
    </div>
  );
}
